import React from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import DashboardHeader from '../../components/dashboard/dashboardHeader'
import { AppContext } from '../../App'
import { Loading } from '../../components/ui/Loading/Loading'
import * as Permission from '../../constants/permission'
import DirectorSuperManagerDashboard from './directorSuperManagerDashboard'
import CadetInterSeniorDashboard from './cadetInterSeniorDashboard'
import ExecAssistDashboard from './execAssistDashboard'

import './dashboardWrapper.css'
import { ModalList, TermsAndConditionsModalProps, TermsAndConditionsModal } from '../../components/termsAndConditions/termsAndConditionsModal'
import * as Request from '../../utilities/request'
import { TermsVersion } from '../../../../back-end/utilities/apiDefinitions'

const initialModalState: {
	modal: string
	props: TermsAndConditionsModalProps
	size: 'sm' | 'md' | 'lg' | 'xl'
} = {
	modal: '',
	props: {} as TermsAndConditionsModalProps,
	size: 'sm',
}
export interface TestingStaff {
	id: string
	role: string
	xpmID: string
}

const Dashboard = () => {
	const { appState } = React.useContext(AppContext)
	const [firmView, setFirmView] = React.useState(false)
	const [testingStaff, setTestingStaff] = React.useState<null | TestingStaff>(null)
	const [shownModal, updateShownModal] = React.useState<{
		modal: string
		props: TermsAndConditionsModalProps
		size: 'sm' | 'md' | 'lg' | 'xl'
	} | null>(initialModalState)

	const permission = appState.permission.roleID === Permission.Admin && testingStaff ? testingStaff.role : appState.permission.roleID
	const isDirector = permission === Permission.Admin || permission === Permission.Director
	const canUseTestingStaff = appState.permission.roleID === Permission.Admin

	const fetchData = async () => {
		const [users, currentAccount, termsVersions] = await Promise.all([
			Request.get('user'),
			Request.get('account'),
			Request.get('termsversion?where=TermsVersionIsBilly==1'),
		])

		const currentTermsVersion =
			termsVersions.data.termsVersions.length > 0
				? termsVersions.data.termsVersions.reduce((a: TermsVersion, b: TermsVersion) =>
						Math.abs(new Date(a.effectiveDate).getTime() - Date.now()) < Math.abs(new Date(b.effectiveDate).getTime() - Date.now()) ? a : b
				  )
				: null

		// First check if this account has the current terms and consitions accepted. If yes, proceed, if not, ask them to accept. If denied also alert.
		if (permission === Permission.Admin && currentTermsVersion) {
			const currentAccountObj = currentAccount.data.accounts.find(
				(a: { accountID: string }) => a.accountID === appState!.authState!.attributes!['custom:currentAccountId']
			)

			if (currentAccountObj.termsAcceptance && currentAccountObj.termsAcceptance.length > 0) {
				const latestAcceptance = currentAccountObj.termsAcceptance.find(
					(ta: { termsVersionID: string }) => ta.termsVersionID === currentTermsVersion.termsVersionID
				)
				if (latestAcceptance) {
					if (latestAcceptance.termsAcceptanceStatus === 'denied') {
						//someone has denied them so show message
						updateShownModal({
							modal: 'TermsAndConditionsAcceptance',
							props: {
								type: 'DeniedAlert',
								product: 'Billy',
								appState: appState,
								latestTermsVersion: currentTermsVersion,
								denialRecord: latestAcceptance,
								denialUser: users.data.users.find((u: { userID: string }) => u.userID === latestAcceptance.userID),
								updateShownModal: updateShownModal,
							},
							size: 'md',
						})
					}
				} else {
					// they have an old version
					updateShownModal({
						modal: 'TermsAndConditionsAcceptance',
						props: {
							type: 'ChooseOption',
							product: 'Billy',
							appState: appState,
							latestTermsVersion: currentTermsVersion,
							updateShownModal: updateShownModal,
						},
						size: 'md',
					})
				}
			} else {
				// never accepted so promp them too
				updateShownModal({
					modal: 'TermsAndConditionsAcceptance',
					props: {
						type: 'ChooseOption',
						product: 'Billy',
						appState: appState,
						latestTermsVersion: currentTermsVersion,
						updateShownModal: updateShownModal,
					},
					size: 'md',
				})
			}
		}
	}

	React.useEffect(
		() => {
			if (appState.authState.isLoggedIn) {
				fetchData()
			}
		},
		// eslint-disable-next-line
		[appState.authState]
	)

	const DashboardBody = () => {
		switch (permission) {
			case Permission.Admin:
			case Permission.Director:
			case Permission.SupervisorManager:
				return (
					<DirectorSuperManagerDashboard
						firmView={firmView}
						isDirector={isDirector}
						testingStaff={canUseTestingStaff && testingStaff ? testingStaff : undefined}
					/>
				)
			case Permission.CadetInterSenior:
				return <CadetInterSeniorDashboard testingStaff={canUseTestingStaff && testingStaff ? testingStaff : undefined} />
			case Permission.ExecAssistant:
				return <ExecAssistDashboard />
			default:
				return (
					<div className="center-flex p-5">
						<Loading show />
					</div>
				)
		}
	}

	return (
		<Container fluid>
			<DashboardHeader
				showFirmViewToggle={isDirector}
				firmView={firmView}
				setFirmView={setFirmView}
				showTestingStaffSelect={canUseTestingStaff}
				testingStaff={testingStaff}
				setTestingStaff={setTestingStaff}
			/>
			<Row className="justify-content-center">
				<Col className="boxed-width">
					<DashboardBody />
				</Col>
			</Row>
			{shownModal ? (
				<ModalList
					handleClose={() => {
						updateShownModal(initialModalState)
					}}
					show={shownModal.modal}
					data={shownModal.props}
					size={shownModal.size}
					style={
						shownModal.props && shownModal.props.type && shownModal.props.type === 'DeniedAlert'
							? { keyboard: true, backdrop: 'static' }
							: undefined
					}
					modals={[
						{
							id: 'TermsAndConditionsAcceptance',
							body: TermsAndConditionsModal,
						},
					]}
				/>
			) : null}
		</Container>
	)
}

export default Dashboard
